import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { API_URL } from "../config";
import { useSpecialty, SPECIALTIES } from "../SpecialtyContext";

const STATUS_LABELS = { open: "Обсуждается", resolved: "Решён" };
const STATUS_COLORS = { open: "#007c84", resolved: "#15803d" };
const PAGE_SIZE = 10;

export default function Cases() {
  const { specialty } = useSpecialty();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterSpec, setFilterSpec] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [showAllTags, setShowAllTags] = useState(false);
  const searchDebounce = useRef(null);

  useEffect(() => {
    setFilterSpec(specialty || "");
    setActiveTag(null);
  }, [specialty]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(searchDebounce.current);
  }, [searchInput]);

  const fetchPage = useCallback((offset) => {
    const params = new URLSearchParams();
    if (filterSpec) params.set("specialty", filterSpec);
    if (search) params.set("search", search);
    params.set("limit", PAGE_SIZE);
    params.set("offset", offset);
    return fetch(`${API_URL}/cases?${params}`).then(r => r.ok ? r.json() : Promise.reject(r.status));
  }, [filterSpec, search]);

  useEffect(() => {
    setLoading(true);
    fetchPage(0)
      .then(data => { setCases(data); setHasMore(data.length === PAGE_SIZE); setError(null); })
      .catch(() => { setError("Не удалось загрузить клинические случаи."); setHasMore(false); })
      .finally(() => setLoading(false));
  }, [fetchPage]);

  const loadMore = () => {
    setLoading(true);
    fetchPage(cases.length)
      .then(data => { setCases(prev => [...prev, ...data]); setHasMore(data.length === PAGE_SIZE); })
      .finally(() => setLoading(false));
  };

  const allTags = [...new Set(
    cases.flatMap(c => c.tags ? c.tags.split(";").map(t => t.trim()).filter(Boolean) : [])
  )];

  const visibleCases = activeTag
    ? cases.filter(c => (c.tags || "").split(";").map(t => t.trim()).includes(activeTag))
    : cases;

  return (
    <section>
      <h1 className="section-title">Клинические случаи</h1>

      {/* Поиск + фильтр специальностей */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <input
          type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
          placeholder="Поиск по случаям…"
          style={{ flex: 1, minWidth: 220, maxWidth: 360, padding: "8px 14px", borderRadius: 8,
            border: "1px solid #c4ceda", fontSize: "0.92rem", outline: "none", boxSizing: "border-box" }}
        />
        <select
          value={filterSpec}
          onChange={e => setFilterSpec(e.target.value)}
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #c4ceda", fontSize: "0.9rem" }}
        >
          <option value="">Все специальности</option>
          {SPECIALTIES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {allTags.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
          {(showAllTags ? allTags : allTags.slice(0, 8)).map(t => (
            <button
              key={t}
              onClick={() => setActiveTag(prev => prev === t ? null : t)}
              style={{
                background: activeTag === t ? "#007c84" : "#f0f2f8",
                color: activeTag === t ? "#fff" : "#444",
                border: "none", borderRadius: 14, padding: "4px 12px",
                fontSize: "0.82rem", fontWeight: 600, cursor: "pointer"
              }}
            >
              {t}
            </button>
          ))}
          {!showAllTags && allTags.length > 8 && (
            <button onClick={() => setShowAllTags(true)}
              style={{ background: "none", color: "#007c84", border: "none",
                fontSize: "0.82rem", cursor: "pointer", fontWeight: 600 }}>
              ещё {allTags.length - 8}
            </button>
          )}
          {activeTag && (
            <button onClick={() => setActiveTag(null)}
              style={{ background: "none", color: "#888", border: "none",
                fontSize: "0.82rem", cursor: "pointer", textDecoration: "underline" }}>
              сбросить
            </button>
          )}
        </div>
      )}

      {error && <p style={{ color: "#c0392b" }}>{error}</p>}

      {!loading && !error && visibleCases.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: 8 }}>🏥</div>
          <p>{search ? "Ничего не найдено." : activeTag ? "Нет случаев с этим тегом." : "Клинических случаев пока нет."}</p>
          {!activeTag && !search && <p style={{ fontSize: "0.88rem" }}>Редакторы могут добавить случаи через Панель управления.</p>}
        </div>
      )}

      {visibleCases.map(c => (
        <div key={c.id} className="card">
          {/* Мета-строка */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            {c.is_anonymous && (
              <span style={{
                background: "#6c757d", color: "#fff",
                borderRadius: 6, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 600
              }}>Анонимно</span>
            )}
            {c.status && (
              <span style={{
                background: STATUS_COLORS[c.status] || "#888", color: "#fff",
                borderRadius: 6, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 600
              }}>{STATUS_LABELS[c.status] || c.status}</span>
            )}
            {c.specialty && (
              <span className="spec-badge">{
                SPECIALTIES.find(s => s.id === c.specialty)?.label || c.specialty
              }</span>
            )}
            <span style={{ marginLeft: "auto", fontSize: "0.85rem", color: "#888" }}>
              {c.created_at ? new Date(c.created_at).toLocaleDateString("ru") : ""}
            </span>
          </div>

          {/* Заголовок */}
          <Link to={`/cases/${c.id}`} style={{ textDecoration: "none" }}>
            <div style={{ fontWeight: 700, fontSize: "1.08rem", color: "#007c84", marginBottom: 4 }}>
              {c.title}
            </div>
          </Link>

          {/* Теги */}
          {c.tags && (
            <div style={{ marginBottom: 6 }}>
              {c.tags.split(";").filter(Boolean).map(t => (
                <span key={t}
                  onClick={() => setActiveTag(t.trim())}
                  style={{
                    display: "inline-block", background: "#e8edf8",
                    color: "#007c84", borderRadius: 5,
                    padding: "1px 8px", fontSize: "0.78rem", marginRight: 4, cursor: "pointer"
                  }}>{t.trim()}</span>
              ))}
            </div>
          )}

          {/* Превью + ссылка */}
          <div style={{ color: "#555", fontSize: "0.95rem", margin: "6px 0", lineHeight: 1.5 }}>
            {(c.content || "").replace(/[#*_`>[\]]/g, "").slice(0, 180)}
            {(c.content || "").length > 180 ? "…" : ""}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <span style={{ fontSize: "0.85rem", color: "#888" }}>
              Автор: {c.is_anonymous ? "Аноним" : (c.author || "—")}
            </span>
            <Link to={`/cases/${c.id}`}
              style={{ background: "none", color: "#e5005b", border: "1.5px solid #e5005b", borderRadius: 7, padding: "4px 14px", fontSize: "0.9rem", textDecoration: "none" }}>
              Открыть →
            </Link>
          </div>
        </div>
      ))}

      {loading && <p style={{ color: "#888", textAlign: "center" }}>Загрузка…</p>}

      {!loading && !error && hasMore && !activeTag && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <button onClick={loadMore}
            style={{ background: "#f0f2f8", color: "#007c84", border: "1.5px solid #c4ceda",
              borderRadius: 8, padding: "8px 24px", cursor: "pointer", fontSize: "0.92rem" }}>
            Показать ещё
          </button>
        </div>
      )}
    </section>
  );
}
